import { useRef, useState, useCallback, useEffect } from 'react';

const TOOLBAR = [
  { cmd: 'bold', icon: 'ri-bold', title: 'Bold' },
  { cmd: 'italic', icon: 'ri-italic', title: 'Italic' },
  { cmd: 'underline', icon: 'ri-underline', title: 'Underline' },
  { divider: true },
  { cmd: 'formatBlock', arg: 'H2', icon: 'ri-heading', title: 'Heading 2' },
  { cmd: 'formatBlock', arg: 'H3', icon: 'ri-h-3', title: 'Heading 3' },
  { divider: true },
  { cmd: 'insertUnorderedList', icon: 'ri-list-unordered', title: 'Bullet List' },
  { cmd: 'insertOrderedList', icon: 'ri-list-ordered', title: 'Numbered List' },
  { divider: true },
  { cmd: 'createLink', icon: 'ri-link', title: 'Insert Link', prompt: true },
  { cmd: 'unlink', icon: 'ri-link-unlink', title: 'Remove Link' },
  { divider: true },
  { cmd: 'removeFormat', icon: 'ri-format-clear', title: 'Clear Formatting' },
  { cmd: 'undo', icon: 'ri-arrow-go-back-line', title: 'Undo' },
  { cmd: 'redo', icon: 'ri-arrow-go-forward-line', title: 'Redo' },
];

interface RichTextEditorProps {
  html: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

export default function RichTextEditor({
  html,
  onChange,
  placeholder = 'Start writing...',
  minHeight = '240px',
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<Record<string, boolean>>({});
  const [showPlaceholder, setShowPlaceholder] = useState(!html || html === '<br>' || html === '<div><br></div>');

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== html) {
      editorRef.current.innerHTML = html;
      setShowPlaceholder(!html || html === '<br>' || html === '<div><br></div>' || !editorRef.current.textContent?.trim());
    }
  }, [html]);

  const updateActiveStates = useCallback(() => {
    const next: Record<string, boolean> = {};
    let formatValue = '';
    try {
      formatValue = document.queryCommandValue('formatBlock')?.toLowerCase() || '';
    } catch {}

    TOOLBAR.forEach((t) => {
      if (!t.divider && t.cmd) {
        try {
          if (t.cmd === 'formatBlock') {
            next[t.cmd + (t.arg || '')] = formatValue === t.arg?.toLowerCase();
          } else {
            next[t.cmd + (t.arg || '')] = document.queryCommandState(t.cmd);
          }
        } catch {
          next[t.cmd + (t.arg || '')] = false;
        }
      }
    });
    setActive(next);
  }, []);

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    const content = el.innerHTML;
    const empty = !el.textContent?.trim();
    setShowPlaceholder(empty);
    onChange(content);
    updateActiveStates();
  };

  const exec = (cmd: string, arg?: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    if (cmd === 'createLink' && arg) {
      document.execCommand(cmd, false, arg);
    } else if (cmd === 'formatBlock') {
      let currentBlock = '';
      try {
        currentBlock = document.queryCommandValue('formatBlock')?.toLowerCase() || '';
      } catch {}
      if (currentBlock === arg?.toLowerCase()) {
        document.execCommand('formatBlock', false, 'p');
      } else {
        document.execCommand(cmd, false, arg);
      }
    } else {
      document.execCommand(cmd, false, undefined);
    }
    handleInput();
  };

  const handleToolbarClick = (item: (typeof TOOLBAR)[number]) => {
    if (item.divider) return;
    if (item.prompt) {
      const url = window.prompt('Enter the URL:');
      if (url) exec(item.cmd!, url);
      return;
    }
    exec(item.cmd!, item.arg);
  };

  const isActive = (item: (typeof TOOLBAR)[number]) => {
    if (item.divider || !item.cmd) return false;
    return active[item.cmd + (item.arg || '')] || false;
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200 flex-wrap">
        {TOOLBAR.map((item, idx) => {
          if (item.divider) {
            return <div key={`div-${idx}`} className="w-px h-5 bg-gray-300 mx-1" />;
          }
          const activeClass = isActive(item)
            ? 'bg-navy/10 text-navy'
            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700';
          return (
            <button
              key={item.cmd! + (item.arg || '')}
              type="button"
              onClick={() => handleToolbarClick(item)}
              title={item.title}
              className={`w-7 h-7 flex items-center justify-center rounded transition-colors cursor-pointer ${activeClass}`}
            >
              <i className={`${item.icon} text-sm`}></i>
            </button>
          );
        })}
      </div>

      {/* Editor area */}
      <div className="relative">
        {showPlaceholder && (
          <div className="absolute top-3 left-3.5 text-sm text-gray-400 pointer-events-none select-none z-10">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="w-full text-sm text-gray-800 px-3.5 py-3 focus:outline-none leading-relaxed rich-text-output"
          style={{ minHeight }}
          onInput={handleInput}
          onKeyUp={updateActiveStates}
          onMouseUp={updateActiveStates}
          onFocus={updateActiveStates}
        />
      </div>
    </div>
  );
}